import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectSchoolId } from "../../../../redux/slices/auth.slice";
import {
  useCreateFun,
  useDeleteFun,
  useFuns,
} from "../../../../services/fun.service";
import {
  AiOutlineDelete,
  AiOutlineFilePdf,
  AiOutlinePlus,
} from "react-icons/ai";
import { Button, Input, Modal, Table, message, Upload } from "antd";

const FunPage = () => {
  const schoolId = useSelector(selectSchoolId);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [fileList, setFileList] = useState([]);

  const { data: funs, refetch } = useFuns(schoolId);
  const createFun = useCreateFun({
    onSuccess: () => {
      message.success("Fun entry created successfully");
      setIsModalVisible(false);
      setName("");
      setDescription("");
      setFileList([]);
      refetch();
    },
    onError: () => {
      message.error("Failed to create fun entry");
    },
  });

  const deleteFun = useDeleteFun({
    onSuccess: () => {
      message.success("Fun entry deleted successfully");
      refetch();
    },
  });

  const handleCreate = () => {
    if (!name || fileList.length === 0) {
      message.warning("Please provide a name and at least one PDF");
      return;
    }
    createFun.mutate({
      name,
      description,
      school: schoolId,
      pdf: fileList.map((f) => f.originFileObj),
    });
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "PDFs",
      dataIndex: "pdf",
      key: "pdf",
      render: (pdfs) => (
        <div className="flex flex-col gap-1">
          {pdfs?.map((pdf, index) => (
            <a
              key={index}
              href={pdf.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-red-500"
            >
              <AiOutlineFilePdf className="mr-1" /> {pdf.name}
            </a>
          ))}
        </div>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="text"
          danger
          icon={<AiOutlineDelete />}
          onClick={() => deleteFun.mutate(record._id)}
        />
      ),
    },
  ];

  return (
    <div className="p-8">
      <div className="bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Fun Activities</h1>
          <Button
            type="primary"
            icon={<AiOutlinePlus />}
            onClick={() => setIsModalVisible(true)}
            className="bg-red-500 hover:bg-red-600 border-none rounded-lg px-4 py-2 mr-4 flex items-center"
          >
            Add New
          </Button>
        </div>

        <Table
          dataSource={funs}
          columns={columns}
          rowKey="_id"
          pagination={{ pageSize: 10 }}
        />

        <Modal
          title="Add New Fun Activity"
          visible={isModalVisible}
          onOk={handleCreate}
          onCancel={() => setIsModalVisible(false)}
          confirmLoading={createFun.isLoading}
        >
          <div className="flex flex-col gap-4">
            <div>
              <label className="block mb-1 font-semibold">Name</label>
              <Input
                placeholder="Enter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label className="block mb-1 font-semibold">Description</label>
              <Input.TextArea
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div>
              <label className="block mb-2 font-semibold text-gray-700">
                Upload PDFs
              </label>
              <Upload
                accept=".pdf"
                multiple
                beforeUpload={(file) => {
                  const isLt10M = file.size / 1024 / 1024 < 20;
                  if (!isLt10M) {
                    message.error(
                      `${file.name} is too large. Please upload files smaller than 20MB.`,
                    );
                    return Upload.LIST_IGNORE;
                  }
                  return false;
                }}
                fileList={fileList}
                onChange={({ fileList }) => setFileList(fileList)}
                className="w-full"
              >
                <div className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-8 cursor-pointer hover:border-red-500 hover:bg-red-50 transition-all group w-full">
                  <AiOutlinePlus className="text-3xl text-gray-400 group-hover:text-red-500 mb-2 transition-colors" />
                  <span className="text-gray-600 group-hover:text-red-500 font-medium">
                    Select PDF Files
                  </span>
                  <span className="text-gray-400 text-xs mt-1">
                    Click or drag files to this area
                  </span>
                </div>
              </Upload>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default FunPage;
